import React, { FC, useCallback, useState } from 'react';

import ConfirmationDialog from './ConfirmationDialog';
import ConfirmationDialogContext from './ConfirmationDialogContext';
import { ConfirmationDialogOptions, ConfirmationDialogOptionsPartial } from './types';

const DEFAULT_OPTIONS: ConfirmationDialogOptions = {
  title: 'Are you sure?',
  confirmationText: 'Yes',
  cancellationText: 'Cancel',
};

type State = {
  parentId: string;
  didConfirm: () => void;
  didCancel: () => void;
};

const ConfirmationDialogProvider: FC<{ children?: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<State | null>(null);
  const [key, setKey] = useState(0);
  const [options, setOptions] = useState<ConfirmationDialogOptions>(DEFAULT_OPTIONS);

  const confirmBase = useCallback((parentId, thisOptions: ConfirmationDialogOptionsPartial = {}): Promise<boolean> => {
    return new Promise((resolve) => {
      setKey((thisKey) => thisKey + 1);
      setOptions({ ...DEFAULT_OPTIONS, ...thisOptions });
      setState({
        parentId,
        didConfirm: () => resolve(true),
        didCancel: () => resolve(false),
      });
    });
  }, []);

  const closeOnParentUnmount = useCallback((thisParentId) => {
    setState((thisState) => {
      if (thisState && thisState.parentId === thisParentId) {
        thisState.didCancel();
        return null;
      }

      return thisState;
    });
  }, []);

  const handleCancel = useCallback(() => {
    setState((thisState) => {
      if (thisState) {
        thisState.didCancel();
      }
      return null;
    });
  }, []);

  const handleConfirm = useCallback(() => {
    setState((thisState) => {
      if (thisState) {
        thisState.didConfirm();
      }
      return null;
    });
  }, []);

  return (
    <>
      <ConfirmationDialogContext.Provider value={{ confirmBase, closeOnParentUnmount }}>
        {children}
      </ConfirmationDialogContext.Provider>
      <ConfirmationDialog
        key={key}
        title={options.title}
        subtitle={options.subtitle}
        doNotShowAgainKey={options.doNotShowAgainKey}
        isOpen={state !== null}
        onClose={handleCancel}
        onCancel={handleCancel}
        onConfirm={handleConfirm}
        cancellationText={options.cancellationText}
        confirmationText={options.confirmationText}
        isDestructive={options.isDestructive}
      >
        {options.content}
      </ConfirmationDialog>
    </>
  );
};

export default ConfirmationDialogProvider;
